import { Category, Event, Subtype, TargetType } from './types'

// The five groupings the events page organises around. `program` is the only
// one that isn't a raw API type — it collapses the fsp/alumni/generic session
// sale types, which are all "a paid program" as far as members are concerned.
export const CATEGORY = {
  CORE_CURRICULUM: 'curriculum',
  GUEST_EXPERT: 'guest',
  COURSE: 'course',
  PROGRAM: 'program',
  CHALLENGE: 'writing_challenge'
} as const

export const CATEGORY_LABELS: Record<Category, string> = {
  curriculum: 'Core Curriculum',
  guest: 'Guest Expert',
  course: 'Course',
  program: 'Program',
  writing_challenge: '14-Day Challenge'
}

export const CATEGORY_LABELS_PLURAL: Record<Category, string> = {
  curriculum: 'Core Curriculum',
  guest: 'Guest Experts',
  course: 'Courses',
  program: 'Programs',
  writing_challenge: '14-Day Challenge'
}

// The single source of truth for subtype display labels, replacing the copies
// that used to live in EventSingle, WebinarSingle, CardWebinar, ResultSingle
// and WebinarIndexPage. Safe as one flat map because webinar.type and
// session_sale.type have no values in common — asserted in categories.test.js.
export const SUBTYPE_LABELS: Record<Subtype, string> = {
  curriculum: 'Core Curriculum',
  guest: 'Guest Expert',
  course: 'Course',
  fsp: 'Faculty Success Program',
  alumni: 'FSP Alumni',
  generic: 'Program',
  writing_challenge: '14-Day Writing Challenge'
}

const WEBINAR_CATEGORIES: Record<string, Category> = {
  curriculum: CATEGORY.CORE_CURRICULUM,
  guest: CATEGORY.GUEST_EXPERT,
  course: CATEGORY.COURSE
}

const SESSION_SALE_CATEGORIES: Record<string, Category> = {
  fsp: CATEGORY.PROGRAM,
  alumni: CATEGORY.PROGRAM,
  generic: CATEGORY.PROGRAM,
  writing_challenge: CATEGORY.CHALLENGE
}

// targetType is the /v2/events envelope `type`; subtype is `target.type`.
export const categoryOf = (targetType: TargetType, subtype: string): Category => {
  const categories = targetType === 'webinar'
    ? WEBINAR_CATEGORIES
    : targetType === 'session_sale' ? SESSION_SALE_CATEGORIES : null

  return categories ? categories[subtype] || null : null
}

export const categoryOfEvent = (event: Event): Category =>
  event && event.target ? categoryOf(event.type, event.target.type) : null

export const labelForSubtype = (subtype: string): string =>
  SUBTYPE_LABELS[subtype] || null

export const labelForCategory = (category: Category): string =>
  CATEGORY_LABELS[category] || null

// Paid offerings get their own section on the events page rather than sitting
// in the live-events list.
export const isPaid = (category: Category): boolean =>
  category === CATEGORY.PROGRAM || category === CATEGORY.CHALLENGE

export const isPaidEvent = (event: Event): boolean => isPaid(categoryOfEvent(event))
